import React from 'react'
import { useTranslation } from 'react-i18next'
import AppBody from '../AppBody'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import { LightCard } from '../../components/Card'
import { ButtonPrimary } from '../../components/Button'
import {
  useDefaultRewardPoolBalance,
  useLiquidityRewardInfo,
  useTotalUnclaimed,
  useUnclaimedReward
} from '../../state/wallet/hooks'
import { useActiveWeb3React } from '../../hooks'
import JSBI from 'jsbi'
import { ethers } from 'ethers'
import { useLiquidityRewardClaimContract } from '../../hooks/useContract'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionResponse } from '@ethersproject/providers'
import { useCallback, useState, useEffect } from 'react'

// 添加骨架屏组件
const Skeleton = ({ width = '100%', height = '24px' }: { width?: string; height?: string }) => (
  <div
    style={{
      width,
      height,
      backgroundColor: '#eee',
      borderRadius: '4px',
      animation: 'pulse 1.5s infinite ease-in-out'
    }}
  />
)

// 添加骨架屏动画样式
const skeletonStyle = `
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`

export default function Earn() {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const [isPending, setIsPending] = useState(false)
  const rewardPoolBalance = useDefaultRewardPoolBalance()
  const { formattedAmount, isLoading, unclaimedAmount, refresh: refreshUnclaimedReward } = useUnclaimedReward(account)
  const { totalUnclaimed, isLoading: totalUnclaimedLoading, refresh: refreshTotalUnclaimed } = useTotalUnclaimed()
  const {
    formattedTotalClaimed,
    isLoading: liquidityLoading,
    liquidityInfo,
    refresh: refreshLiquidityInfo
  } = useLiquidityRewardInfo(account)

  // 计算实际奖池剩余
  const actualPoolBalance = (() => {
    if (!rewardPoolBalance?.raw || !totalUnclaimed) return null

    const remaining = JSBI.subtract(rewardPoolBalance.raw, totalUnclaimed)
    const etherValue = ethers.utils.formatEther(remaining.toString())
    const numValue = parseFloat(etherValue)

    return numValue < 0 ? 0 : numValue
  })()

  // 获取合约实例
  const liquidityRewardClaimContract = useLiquidityRewardClaimContract()
  // 获取交易添加器
  const addTransaction = useTransactionAdder()

  // 添加自动刷新功能
  useEffect(() => {
    // 创建定时器，每12秒刷新一次数据
    const refreshInterval = setInterval(() => {
      if (account) {
        refreshUnclaimedReward()
        refreshLiquidityInfo()
        refreshTotalUnclaimed()
      }
    }, 12000)

    // 组件卸载时清除定时器
    return () => clearInterval(refreshInterval)
  }, [account, refreshUnclaimedReward, refreshLiquidityInfo, refreshTotalUnclaimed])

  // 处理提取奖励
  const handleClaimReward = useCallback(async () => {
    if (!liquidityRewardClaimContract || !account || !unclaimedAmount || JSBI.EQ(unclaimedAmount, JSBI.BigInt(0)))
      return

    try {
      setIsPending(true)
      const response: TransactionResponse = await liquidityRewardClaimContract.claimReward()

      addTransaction(response, {
        summary: t('claimRewardSuccess', { amount: formattedAmount })
      })

      await response.wait()

      // 提取完成后刷新数据
      refreshUnclaimedReward()
      refreshLiquidityInfo()
    } catch (error) {
      console.error(t('claimRewardFailed'), error)
    } finally {
      setIsPending(false)
    }
  }, [
    liquidityRewardClaimContract,
    account,
    unclaimedAmount,
    formattedAmount,
    addTransaction,
    refreshUnclaimedReward,
    refreshLiquidityInfo,
    t
  ])

  // 计算总收益 (已提取 + 可提取)，使用JSBI进行安全计算
  const totalReward =
    !isLoading && !liquidityLoading
      ? JSBI.add(unclaimedAmount || JSBI.BigInt(0), liquidityInfo.totalClaimed || JSBI.BigInt(0))
      : null

  // 格式化totalReward以便显示
  const formattedTotalReward = (() => {
    if (!totalReward) return '0'

    // 先使用ethers.utils.formatEther将JSBI转换为以太坊格式的字符串
    const etherValue = ethers.utils.formatEther(totalReward.toString())
    const numValue = parseFloat(etherValue)

    if (numValue === 0) return '0'
    if (numValue < 0.0001) return '<0.0001'

    // 截断保留5位小数（不四舍五入）
    return (Math.floor(numValue * 10000) / 10000).toFixed(4)
  })()

  return (
    <>
      <style>{skeletonStyle}</style>
      <AppBody>
        <SwapPoolTabs active={'earn'} />
        <LightCard padding={'10px'} width={'100%'}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('poolRemaining')}</span>
            <span style={{ opacity: 0.5 }}>MUD</span>
          </div>
          {!actualPoolBalance || totalUnclaimedLoading ? (
            <Skeleton height="30px" width="100%" />
          ) : (
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{actualPoolBalance.toFixed(2)}</div>
          )}
        </LightCard>
        <div style={{ display: 'flex', gap: '16px', width: '100%', marginBottom: 20, marginTop: 20 }}>
          <LightCard padding={'10px'} width={'100%'}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('totalEarnings')}</span>
              <span style={{ opacity: 0.5 }}>MUD</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {isLoading || liquidityLoading ? <Skeleton height="30px" width="100%" /> : formattedTotalReward}
            </div>
          </LightCard>
          <LightCard padding={'10px'} width={'50%'}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('claimed')}</span>
              <span style={{ opacity: 0.5 }}>MUD</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {liquidityLoading ? <Skeleton height="30px" width="100%" /> : `${formattedTotalClaimed}`}
            </div>
          </LightCard>
        </div>
        <LightCard padding={'10px'} width={'100%'}>
          <div style={{ textAlign: 'center' }}>{t('claimable')}</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', textAlign: 'center' }}>
            {isLoading ? <Skeleton height="30px" width="100%" /> : `${formattedAmount}`}
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('fee')}</span>
              <span>0 MUD</span>
            </div>
          </div>
          <ButtonPrimary
            id="join-pool-button"
            style={{ padding: 16, marginTop: 20 }}
            onClick={handleClaimReward}
            disabled={
              isPending || isLoading || !unclaimedAmount || JSBI.EQ(unclaimedAmount || JSBI.BigInt(0), JSBI.BigInt(0))
            }
          >
            {isPending ? t('claiming') : t('claim')}
          </ButtonPrimary>
        </LightCard>
      </AppBody>
    </>
  )
}
